import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchCurrentSession } from '../../data/auth';
import { isPreviewRuntime, resolveRuntimeOrigin } from '../../lib/runtimeEnv';
import { consumeStoredReturnPath, validateReturnPath } from '../../auth/routePolicy';

const SESSION_RETRY_DELAYS_MS = [0, 350, 750, 1_250, 2_000, 3_000];

type CallbackError = {
  code: string | null;
  description: string | null;
};

function readParams(value: string, prefix: '?' | '#'): URLSearchParams {
  return new URLSearchParams(value.startsWith(prefix) ? value.slice(1) : value);
}

export function resolveAuthCallbackTarget(search: string): string {
  return validateReturnPath(readParams(search, '?').get('from')) ?? '/';
}

export function isClientPortalCallbackTarget(target: string): boolean {
  return target === '/client-portal'
    || target.startsWith('/client-portal/')
    || target.startsWith('/client-portal?');
}

export function readAuthCallbackError(search: string, hash: string): CallbackError | null {
  const query = readParams(search, '?');
  const fragment = readParams(hash, '#');
  const code = query.get('error_code')
    || fragment.get('error_code')
    || query.get('error')
    || fragment.get('error');
  const description = query.get('error_description')
    || fragment.get('error_description');
  return code || description ? { code, description } : null;
}

function portalErrorMessage(error?: CallbackError | null): string {
  if (error?.code === 'otp_expired') {
    return 'This secure sign-in link has expired or has already been used.';
  }
  return 'The secure sign-in could not be completed. The link may be expired or already used.';
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const target = React.useMemo(
    () => resolveAuthCallbackTarget(location.search),
    [location.search],
  );
  const isClientPortalTarget = isClientPortalCallbackTarget(target);

  React.useEffect(() => {
    let mounted = true;
    let retryTimeout: number | undefined;
    let attempt = 0;
    const hash = location.hash;

    if (hash.includes('type=recovery')) {
      // Keep the fragment so the reset-password page can use the recovery token.
      navigate(`/reset-password${hash}`, { replace: true });
      return () => {
        mounted = false;
      };
    }

    const callbackError = readAuthCallbackError(location.search, hash);
    if (callbackError) {
      if (isClientPortalTarget) {
        setErrorMessage(portalErrorMessage(callbackError));
      } else {
        const loginSearch = new URLSearchParams();
        if (callbackError.code) loginSearch.set('error_code', callbackError.code);
        navigate(`/login${loginSearch.size ? `?${loginSearch.toString()}` : ''}`, {
          replace: true,
          state: { from: target },
        });
      }
      return () => {
        mounted = false;
      };
    }

    const checkSession = async () => {
      const { data } = await fetchCurrentSession();
      if (!mounted) return;
      if (data?.session) {
        // The callback target was already validated above. Consume the
        // coordinator's one-shot recovery path only after authentication is
        // actually established so a later sign-in cannot inherit it.
        consumeStoredReturnPath();
        if (isPreviewRuntime()) {
          // eslint-disable-next-line no-console
          console.info('[diag] callback: origin', resolveRuntimeOrigin() ?? '(empty)');
          // eslint-disable-next-line no-console
          console.info('[diag] callback: next step', target);
        }
        navigate(target, { replace: true });
        return;
      }

      if (attempt < SESSION_RETRY_DELAYS_MS.length) {
        const delay = SESSION_RETRY_DELAYS_MS[attempt];
        attempt += 1;
        retryTimeout = window.setTimeout(() => {
          void checkSession();
        }, delay);
        return;
      }

      if (isClientPortalTarget) {
        setErrorMessage(portalErrorMessage());
      } else {
        if (isPreviewRuntime()) {
          // eslint-disable-next-line no-console
          console.info('[diag] callback: origin', resolveRuntimeOrigin() ?? '(empty)');
          // eslint-disable-next-line no-console
          console.info('[diag] callback: next step', '/login');
        }
        navigate('/login?error_code=session_not_ready', {
          replace: true,
          state: { from: target },
        });
      }
    };

    void checkSession().catch((error) => {
      // fetchCurrentSession normally normalizes errors, but keep this callback
      // recoverable if a future implementation throws unexpectedly.
      // eslint-disable-next-line no-console
      console.error('[auth-callback] session resolution failed:', error);
      if (!mounted) return;
      if (isClientPortalTarget) {
        setErrorMessage(portalErrorMessage());
      } else {
        navigate('/login?error_code=session_not_ready', {
          replace: true,
          state: { from: target },
        });
      }
    });

    return () => {
      mounted = false;
      if (retryTimeout !== undefined) {
        window.clearTimeout(retryTimeout);
      }
    };
  }, [isClientPortalTarget, location.hash, location.search, navigate, target]);

  if (errorMessage) {
    return (
      <main className="section-shell stack gap-md" style={{ maxWidth: 560, margin: '3rem auto' }}>
        <div className="alert alert-error" role="alert">
          <div className="stack gap-sm">
            <h1 className="m0">This client portal link could not sign you in</h1>
            <p className="m0">{errorMessage}</p>
            <p className="m0">
              Return to the client portal to request a new email link, or ask your ITA contact
              to send you a new link.
            </p>
          </div>
        </div>
        <div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate(target, { replace: true })}
          >
            Return to client portal
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="section-shell stack gap-sm" style={{ maxWidth: 520, margin: '3rem auto' }}>
      <h1 className="m0">Completing secure sign-in...</h1>
      <p className="m0">
        {isClientPortalTarget
          ? 'Please wait while we open the client portal.'
          : 'Please wait while we open your workspace.'}
      </p>
    </main>
  );
}
